import type { DiscoveryV2ListResponseType } from "@sellar-io/api-v2/types";
import { Icon } from "@sellar-io/components";
import { truncate } from "@sellar-io/common";
import { useCallback, useMemo, useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { Toast } from "../../../../components/toast/Toast";
import { useToast } from "../../../../components/toast/useToast.shared";
import { useEvents } from "../../../../hooks";
import {
  useUnifiedCheckoutBasketAddItem,
  useUnifiedCheckoutBasketList,
  useUnifiedCheckoutBasketUpdateItem,
} from "../../../../hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { useConsolidatedOrderingIntercom } from "../../../../hooks/useConsolidatedOrderingIntercom";
import { useMarketStorefrontSessionId } from "../../../../hooks/useMarketStorefrontSessionTracking";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import {
  type ConsolidatedBasketOpenSource,
  getConsolidatedBasketIntercomData,
  getConsolidatedBasketProperties,
  useConsolidatedBasketTracking,
} from "./consolidatedOrderingTracking";
import { useMarketBasketFeedback } from "./useMarketBasketFeedback";

type DiscoveryProduct = DiscoveryV2ListResponseType[number];

export type ConsolidatedBasketProduct = Pick<
  DiscoveryProduct,
  | "id"
  | "name"
  | "availableStock"
  | "canBackorder"
  | "price"
  | "containerType"
  | "priceIsFromConnection"
  | "matchingDeliveryDate"
  | "matchingOrderCutoffDate"
  | "earliestDeliveryDate"
  | "earliestOrderCutoffDate"
> & {
  Parent: Pick<
    NonNullable<DiscoveryProduct["Parent"]>,
    "id" | "heroImageUrl" | "imageUrl"
  > | null;
  Supplier: Pick<DiscoveryProduct["Supplier"], "id" | "urlId" | "Business">;
};

export const useConsolidatedBasket = (options?: {
  onOpenBasket?: (source: ConsolidatedBasketOpenSource) => void;
}) => {
  const { business } = useSellarCurrentBusiness();
  const enabled = useRemoteFeatureFlags().isActive("sdConsolidatedCheckout");
  const isDesktop = useMediaQuery("(min-width: 1084px)");
  const [basketOpen, setBasketOpen] = useState(false);
  const [optimisticQuantities, setOptimisticQuantities] = useState<
    Record<number, number>
  >({});
  const [mutatingProductIds, setMutatingProductIds] = useState<Set<number>>(
    new Set(),
  );
  const { toast } = useToast();
  const { showFlyover } = useMarketBasketFeedback();
  const { track } = useEvents();
  const { trackBasketUpdated } = useConsolidatedOrderingIntercom();
  const { trackBasketOpened } = useConsolidatedBasketTracking();
  const session = useMarketStorefrontSessionId();
  const basketsQuery = useUnifiedCheckoutBasketList();
  const { mutateAsync: addItem } = useUnifiedCheckoutBasketAddItem();
  const { mutateAsync: updateItem } = useUnifiedCheckoutBasketUpdateItem();

  const baskets = useMemo(() => basketsQuery.data ?? [], [basketsQuery.data]);

  const basketEntries = useMemo(
    () =>
      baskets.flatMap((basket) =>
        basket.DraftOrders.flatMap((draft) =>
          draft.Items.map((item) => ({ basket, draft, item })),
        ),
      ),
    [baskets],
  );

  const openBasket = useCallback(
    (source: ConsolidatedBasketOpenSource = "navbar") => {
      if (options?.onOpenBasket) {
        options.onOpenBasket(source);
        return;
      }
      trackBasketOpened(baskets, source);
      setBasketOpen(true);
    },
    [baskets, options, trackBasketOpened],
  );

  const closeBasket = useCallback(() => setBasketOpen(false), []);

  const getBasketEntry = useCallback(
    (productId: number) =>
      basketEntries.find((entry) => entry.item.ProductId === productId),
    [basketEntries],
  );

  const getItemQuantity = useCallback(
    (productId: number) =>
      optimisticQuantities[productId] ??
      getBasketEntry(productId)?.item.quantity ??
      0,
    [getBasketEntry, optimisticQuantities],
  );

  const isItemMutating = useCallback(
    (productId: number) => mutatingProductIds.has(productId),
    [mutatingProductIds],
  );

  const isItemEnabled = useCallback(
    (product: ConsolidatedBasketProduct) =>
      Boolean(
        business?.Retailer?.id &&
        product.Parent?.id &&
        (product.availableStock > 0 || product.canBackorder) &&
        product.price !== null,
      ),
    [business?.Retailer?.id],
  );

  const setMutating = (productId: number, mutating: boolean) => {
    setMutatingProductIds((current) => {
      const next = new Set(current);
      if (mutating) next.add(productId);
      else next.delete(productId);
      return next;
    });
  };

  const showUpdatedToast = useCallback(
    (product: ConsolidatedBasketProduct, message: string) => {
      const [productName, variantName] = (product.name ?? "Item")
        .split("•")
        .map((value) => value.trim());

      toast({
        render: () => (
          <button
            className="w-full text-left"
            onClick={() => openBasket("toast")}
            type="button"
          >
            <Toast
              className="w-full max-w-150 bg-[#A34FD2] md:min-w-150"
              icon="SHOPPING_BASKET"
            >
              <div className="flex w-full flex-wrap items-center justify-between gap-1 pl-4 text-base font-semibold md:flex-nowrap md:gap-2">
                {message}
                <span className="flex items-center gap-4 font-normal">
                  {truncate(productName ?? "")}{" "}
                  {variantName && `• ${variantName}`}
                  <Icon className="flex-none" icon="CHEVRON_RIGHT" size="1" />
                </span>
              </div>
            </Toast>
          </button>
        ),
        placement: "bottom",
      });
    },
    [openBasket, toast],
  );

  const trackUpdate = useCallback(
    (
      basket: NonNullable<typeof basketsQuery.data>[number],
      product: ConsolidatedBasketProduct,
      removed = false,
    ) => {
      track(removed ? "Product removed from cart" : "Product added to cart", {
        ...getConsolidatedBasketProperties(basket, session),
        context: "unified",
        supplierId: product.Supplier.id,
        productId: product.id,
      });

      trackBasketUpdated({
        journeyKey: basket.uuid,
        ...getConsolidatedBasketIntercomData(basket, session),
      });
    },
    [session, track, trackBasketUpdated],
  );

  const addItemToBasket = useCallback(
    async (product: ConsolidatedBasketProduct) => {
      if (!isItemEnabled(product)) return;
      setOptimisticQuantities((current) => ({ ...current, [product.id]: 1 }));
      setMutating(product.id, true);
      try {
        const deliveryDate =
          product.matchingDeliveryDate ?? product.earliestDeliveryDate;
        const cutoffDate =
          product.matchingOrderCutoffDate ?? product.earliestOrderCutoffDate;
        const basket = await addItem({
          SupplierId: product.Supplier.id,
          ProductId: product.id,
          quantity: 1,
          expectedDeliveryAt: deliveryDate ? new Date(deliveryDate) : null,
          orderCutoffAt: cutoffDate ? new Date(cutoffDate) : null,
        });
        setOptimisticQuantities((current) => {
          const next = { ...current };
          delete next[product.id];
          return next;
        });
        trackUpdate(basket, product);
        const hasSupplierInBasket = baskets.some((entry) =>
          entry.DraftOrders.some(
            (draft) => draft.SupplierId === product.Supplier.id,
          ),
        );
        if (isDesktop && !hasSupplierInBasket) {
          showFlyover({
            name: product.name ?? "Item",
            quantity: 1,
            heroImageUrl:
              product.Parent?.heroImageUrl ?? product.Parent?.imageUrl ?? null,
            productId: product.id,
            supplierId: product.Supplier.id,
            supplierName: product.Supplier.Business.name,
            supplierUrlId: product.Supplier.urlId,
            containerType: product.containerType ?? "",
            sdBasketId: basket.uuid,
            price: product.price,
            priceIsFromConnection: product.priceIsFromConnection,
          });
        } else {
          showUpdatedToast(product, "Added to basket");
        }
      } catch (error) {
        setOptimisticQuantities((current) => {
          const next = { ...current };
          delete next[product.id];
          return next;
        });
        throw error;
      } finally {
        setMutating(product.id, false);
      }
    },
    [
      addItem,
      baskets,
      isDesktop,
      isItemEnabled,
      showFlyover,
      showUpdatedToast,
      trackUpdate,
    ],
  );

  const updateItemQuantity = useCallback(
    async (product: ConsolidatedBasketProduct, quantity: number) => {
      const entry = getBasketEntry(product.id);

      if (!entry || !business?.Retailer?.id) return;

      const nextQuantity = Math.max(0, quantity);

      setOptimisticQuantities((current) => ({
        ...current,
        [product.id]: nextQuantity,
      }));

      setMutating(product.id, true);

      try {
        const basket = await updateItem({
          basketUuid: entry.basket.uuid,
          itemId: entry.item.id,
          quantity: nextQuantity,
        });

        setOptimisticQuantities((current) => {
          const next = { ...current };
          delete next[product.id];
          return next;
        });

        if (basket) {
          trackUpdate(basket, product, nextQuantity === 0);
        }

        if (basket && nextQuantity > 0) {
          showUpdatedToast(product, "Basket updated");
        }
      } catch (error) {
        setOptimisticQuantities((current) => {
          const next = { ...current };
          delete next[product.id];
          return next;
        });
        throw error;
      } finally {
        setMutating(product.id, false);
      }
    },
    [
      business?.Retailer?.id,
      getBasketEntry,
      showUpdatedToast,
      trackUpdate,
      updateItem,
    ],
  );

  const totalItemCount = basketEntries.reduce(
    (total, { item }) => total + item.quantity,
    0,
  );

  return {
    enabled,
    basketOpen,
    totalItemCount,
    openBasket,
    closeBasket,
    addItemToBasket,
    updateItemQuantity,
    getItemQuantity,
    isItemEnabled,
    isItemMutating,
  };
};
